import React, { useMemo } from 'react'
import {
  Autocomplete,
  AutocompleteOption,
  FormControl,
  FormLabel,
  ListItemContent,
  Typography,
  type AutocompleteProps,
} from '@mui/joy'

interface SelectOption {
  value: number | string
  label: string
  subtitle?: string
}

interface SearchableSelectProps extends Omit<AutocompleteProps<SelectOption, false, false, false>, 'options' | 'value' | 'onChange'> {
  options: SelectOption[]
  value: number | string | null
  onChange: (value: number | string | null) => void
  label?: string
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  label,
  placeholder = 'Search...',
  ...props
}) => {
  const selectedOption = useMemo(
    () => options.find(opt => opt.value === value) || null,
    [options, value]
  )

  return (
    <FormControl>
      {label && <FormLabel>{label}</FormLabel>}
      <Autocomplete
        {...props}
        placeholder={placeholder}
        options={options}
        value={selectedOption}
        onChange={(_, newValue) => {
          onChange(newValue ? newValue.value : null)
        }}
        getOptionLabel={(option) => option.label}
        renderOption={(props, option) => (
          <AutocompleteOption {...props}>
            <ListItemContent>
              <Typography level="body-sm">{option.label}</Typography>
              {option.subtitle && (
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  {option.subtitle}
                </Typography>
              )}
            </ListItemContent>
          </AutocompleteOption>
        )}
        isOptionEqualToValue={(option, value) => option.value === value.value}
      />
    </FormControl>
  )
}